// Where will this order be filled?
//
// Bloom is fulfilled by two pharmacies:
//   • Blue Five Labs  — peptides only, ships all 50 states
//   • Greenstone Rx   — FDA-approved drugs/compounds, ships ~45 states
//
// The authoritative routing decision lives in pharmacy terminal / compoundos.
// This module is a LOCAL, display-only mirror so doctors and AMs can see where
// an order will land before/after submitting. It is intentionally not wired
// into the actual pharmacy submission path (see PharmacyRoutingService).
//
// Business rule (order-level — an order is never split across pharmacies):
//   1. If ANY item is a peptide (Blue Five Labs–only) → the whole order is
//      filled by Blue Five Labs.
//   2. Otherwise (all FDA compounds) → Greenstone Rx, UNLESS Greenstone does
//      not ship to the destination state, in which case the lab (Blue Five
//      Labs) fills it instead.

import { US_STATE_OPTIONS } from './usStates';

export type FulfillmentPharmacyKey = 'bluefive' | 'greenstone';

export interface PharmacyInfo {
  key: FulfillmentPharmacyKey;
  name: string;      // Full display name
  shortName: string; // Compact label for tight UI
  blurb: string;     // One-line "what they fill" descriptor for tooltips
}

export const PHARMACIES: Record<FulfillmentPharmacyKey, PharmacyInfo> = {
  bluefive: {
    key: 'bluefive',
    name: 'Blue Five Labs',
    shortName: 'Blue Five',
    blurb: 'Peptides · ships all 50 states',
  },
  greenstone: {
    key: 'greenstone',
    name: 'Greenstone Rx',
    shortName: 'Greenstone',
    blurb: 'FDA-approved drugs & compounds',
  },
};

// States Greenstone Rx does NOT ship to. When an all-FDA order ships to one of
// these, Blue Five Labs ("the lab") fills it instead (rule #2 above).
//
// Source of truth: the compoundos routing_rules in pharmacyV2 (Greenstone =
// ncpdpid 5759608) list 43 covered jurisdictions; these 8 states are the
// complement. Cross-checked against real order history — Greenstone has never
// shipped to AK/HI/SC and only leaked a handful of pre-rule orders to the rest.
// If the pharmacy's licensing changes, update this list to match routing_rules.
export const GREENSTONE_EXCLUDED_STATES = new Set<string>([
  'AK', 'AR', 'CA', 'GA', 'HI', 'MA', 'NC', 'SC',
]);

export interface RoutingItem {
  isPeptide?: boolean;
}

const STATE_NAME_TO_CODE: Record<string, string> = Object.fromEntries(
  US_STATE_OPTIONS.map(s => [s.name.toLowerCase(), s.code]),
);

/** Normalize a state to a 2-letter uppercase code, or null if unknown. */
export function normalizeState(state?: string | null): string | null {
  if (!state) return null;
  const trimmed = state.trim();
  if (trimmed.length === 2) return trimmed.toUpperCase();
  return STATE_NAME_TO_CODE[trimmed.toLowerCase()] ?? null;
}

/**
 * Map order line items (which only snapshot productId) to routing items using a
 * productId→isPeptide lookup. Returns null until EVERY item is resolved, so
 * callers don't briefly render the wrong pharmacy while the map is still loading.
 */
export function itemsWithPeptide<T extends { productId: string }>(
  items: T[] | null | undefined,
  peptideMap: Record<string, boolean>
): RoutingItem[] | null {
  if (!items || items.length === 0) return null;
  const out: RoutingItem[] = [];
  for (const item of items) {
    const id = String(item.productId);
    if (!(id in peptideMap)) return null; // not fully resolved yet
    out.push({ isPeptide: peptideMap[id] });
  }
  return out;
}

/**
 * Resolve which single pharmacy will fill an order.
 * Returns null when there are no items to evaluate.
 */
export function resolveFulfillmentPharmacy(
  items: RoutingItem[] | null | undefined,
  shipToState?: string | null
): PharmacyInfo | null {
  if (!items || items.length === 0) return null;

  // Rule 1: any peptide → the whole order goes to Blue Five Labs.
  if (items.some((i) => i.isPeptide)) return PHARMACIES.bluefive;

  // Rule 2: all FDA compounds → Greenstone, unless it can't ship to the state.
  const code = normalizeState(shipToState);
  if (code && GREENSTONE_EXCLUDED_STATES.has(code)) return PHARMACIES.bluefive;
  return PHARMACIES.greenstone;
}
